import React, {createRef, ReactNode, RefObject} from "react";
import DomHelper from "../../../../shared/helper/DomHelper";
import MouseMode from "../../../viewer/settings/MouseMode";
import MouseFunction from "../../../viewer/settings/MouseFunction";
import {Point, Rectangle} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import NumberHelper from "../../../../shared/helper/NumberHelper";
import {I18nGlobals} from "../../../../shared/translation/i18nInterface";
import MenuButton from "../../../../shared/menu/MenuButton";
import Translate from "../../../../shared/translation/Translate";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import AnnotationEditBoxDialog from "../../../dialog/components/annotation/AnnotationEditBoxDialog";
import PortalContext from "../../../PortalContext";
import Immutable from "../../../../shared/immutable/Immutable";
import UserSettings from "../../../session/UserSettings";
import {DebugGlobals} from "../../../dialog/components/settings/DebugDialog";
import {ContextMenu} from "primereact/contextmenu";
import PortalPureComponent from "../../../../shared/components/PortalPureComponent";
import {MenuItem} from "primereact/menuitem";

interface PointSelectionProperties extends I18nGlobals, DebugGlobals {
	mouseMode?: MouseMode;
	mouseFunction?: MouseFunction;
	zoomValue?: number;
	startCallback?: (options: Point | null, event?: React.MouseEvent) => void;
	moveCallback?: (options: Point | null, event?: MouseEvent) => void;
	endCallback?: (options: Point | null, apply: boolean) => void;
	handleInteractivity?: () => void;
	confirmation?: boolean;
	context: PortalContext;
	userSettings?: Immutable<UserSettings>;
	getParent: () => HTMLElement | null;
}

interface PointSelectionState {
	point: Point;
	active: boolean;
	edit: boolean;
	showBoxEditDialog: boolean;
}

export default class PointSelection extends PortalPureComponent<PointSelectionProperties, PointSelectionState> {
	private checking: boolean;
	private contextMenu: RefObject<ContextMenu>;

	constructor(props: Readonly<PointSelectionProperties>) {
		super(props);

		this.checking = false;
		this.state = {
			point: Point.fromJson({
				x: 0,
				y: 0
			}),
			active: false,
			edit: false,
			showBoxEditDialog: false
		};

		this.contextMenu = createRef<ContextMenu>();

		this.handlePointerDown = this.handlePointerDown.bind(this);
		this.handlePointerMove = this.handlePointerMove.bind(this);
		this.handlePointerUp = this.handlePointerUp.bind(this);
		this.handleMove = this.handleMove.bind(this);
		this.handleKeyDown = this.handleKeyDown.bind(this);
		this.handleClick = this.handleClick.bind(this);
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();

		let parent: HTMLElement | null = this.props.getParent();
		if (FunctionHelper.exists(parent)) {
			parent!.addEventListener("pointerdown", this.handlePointerDown);
		}

		// apply global events so events get applied outside the browser
		window.addEventListener("pointerup", this.handlePointerUp);
		window.addEventListener("pointermove", this.handlePointerMove);
		window.addEventListener("keydown", this.handleKeyDown);
	}

	/**
	 * This is called when the component is unmounted
	 */
	public async componentWillUnmount(): Promise<void> {
		await super.componentWillUnmount();

		let parent: HTMLElement | null = this.props.getParent();
		if (FunctionHelper.exists(parent)) {
			parent!.removeEventListener("pointerdown", this.handlePointerDown);
		}

		window.removeEventListener("pointerup", this.handlePointerUp);
		window.removeEventListener("pointermove", this.handlePointerMove);
		window.removeEventListener("keydown", this.handleKeyDown);
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<PointSelectionProperties>, prevState: Readonly<PointSelectionState>, snapshot?: any
	): Promise<void> {
		let updates: any = {};

		if (this.props.mouseMode !== prevProps.mouseMode || this.props.mouseFunction !== prevProps.mouseFunction) {
			updates.active = false;
		}

		let zoomScale: number = FunctionHelper.applyValue(this.props.zoomValue, 1) / 100;
		let previousZoomScale: number = FunctionHelper.applyValue(prevProps.zoomValue, 1) / 100;
		if (zoomScale !== previousZoomScale) {
			updates = {
				...updates,
				point: Point.fromJson({
					x: (this.state.point.x! / previousZoomScale) * zoomScale,
					y: (this.state.point.y! / previousZoomScale) * zoomScale,
				})
			} as PointSelectionState
		}

		if (Object.keys(updates).length > 0) {
			this.setState(updates);
		}
	}

	private async handleKeyDown(event: KeyboardEvent): Promise<void> {
		let parent: HTMLElement | null = this.props.getParent();
		if (!FunctionHelper.exists(parent) || !this.state.active || this.state.showBoxEditDialog) {
			return;
		}

		let point: Point = this.state.point;
		let containerRect: DOMRect = parent!.getBoundingClientRect();
		let mod: number = event.ctrlKey ? 5 : 1;

		switch (event.key.toLowerCase()) {
			case "arrowup":
				event.preventDefault();
				if (point.y! - mod <= 0) {
					point.y! = 0;
					return;
				}

				point.y! -= mod;
				break;
			case "arrowdown":
				event.preventDefault();
				if (point.y! + mod >= containerRect.height) {
					point.y! = containerRect.height;
					return;
				}

				point.y! += mod;
				break;
			case "arrowleft":
				event.preventDefault();
				if (point.x! - mod <= 0) {
					point.x! = 0;
					return;
				}

				point.x! -= mod;
				break;
			case "arrowright":
				event.preventDefault();
				if (point.x! + mod >= containerRect.width) {
					point.x! = containerRect.width;
					return;
				}

				point.x! += mod;
				break;
		}

		this.setState({
			point: point.clone()
		}, async () => {
			if (FunctionHelper.exists(this.props.endCallback)) {
				await this.props.endCallback!(this.state.point, true);
			}
		});
	}

	private async handlePointerUp(event: MouseEvent): Promise<void> {
		if (!this.state.active || !this.state.edit || event.button !== 0) {
			return;
		}

		event.stopPropagation();

		if (FunctionHelper.exists(this.props.endCallback)) {
			this.props.endCallback!(this.state.point, !this.props.confirmation);
		}

		this.setState({
			edit: false
		});
	}

	private handlePointerDown(event: MouseEvent): void {
		let parent: HTMLElement | null = this.props.getParent();
		if (!FunctionHelper.exists(parent) || event.button !== 0 ||
			!DomHelper.hasClass(event.target as HTMLElement, parent!.className)) {
			return;
		}

		event.stopPropagation();

		if (FunctionHelper.exists(this.props.handleInteractivity)) {
			this.props.handleInteractivity!();
		}

		this.setState({
			active: true,
			edit: true
		}, () => {
			if (FunctionHelper.exists(this.props.startCallback)) {
				this.props.startCallback!(this.state.point);
			}

			this.handlePointerMove(event);
		});
	}

	private handlePointerMove(event: MouseEvent): void {
		let parent: HTMLElement | null = this.props.getParent();
		if (!FunctionHelper.exists(parent) || !this.state.active || !this.state.edit || this.checking) {
			return;
		}

		event.stopPropagation();

		this.checking = true;
		let containerRect: DOMRect = parent!.getBoundingClientRect();

		let xPos: number = NumberHelper.limit(event.clientX - containerRect.x, 0, containerRect.width);
		let yPos: number = NumberHelper.limit(event.clientY - containerRect.y, 0, containerRect.height);

		this.setState({
			point: Point.fromJson({
				x: xPos,
				y: yPos
			})
		}, async () => {
			if (FunctionHelper.exists(this.props.moveCallback)) {
				await this.props.moveCallback!(this.state.point, event);
			}

			this.checking = false;
		});
	}

	private handleMove(event: React.MouseEvent): void {
		if (event.button !== 0) {
			return;
		}

		event.stopPropagation();

		this.setState({
			edit: true
		});
	}

	private handleClick(e: React.MouseEvent): void {
		if (e.button === 2 && FunctionHelper.exists(this.contextMenu.current)) {
			e.preventDefault();
			e.stopPropagation();
			this.contextMenu.current!.show(e);
		}
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		let self: PointSelection = this;
		let selectionClasses: Array<string> = ["point-selection"];

		let previewTypes: Array<MouseFunction> = [
			MouseFunction.NOTE
		];

		if (previewTypes.includes(this.props.mouseFunction!)) {
			selectionClasses.push("preview");
		}

		if (this.state.active) {
			selectionClasses.push("active");
		}

		let zoomScale: number = FunctionHelper.applyValue(this.props.zoomValue, 1) / 100;
		let width: number = 0;
		let height: number = 0;

		if (this.props.mouseFunction === MouseFunction.NOTE) {
			width = 20 * zoomScale;
			height = 20 * zoomScale;
		}

		let contextMenu: Array<MenuItem> = [
			{
				label: Translate.value("buttons.cancel"),
				icon: 'icon icon-close',
				command(e) {
					if (FunctionHelper.exists(self.props.endCallback)) {
						self.props.endCallback!(null, false);
					}

					self.setState({
						active: false
					});
				}
			}
		];

		if (this.props.confirmation) {
			contextMenu = [{
				label: Translate.value("buttons.confirm"),
				icon: 'icon icon-check',
				command(e) {
					if (FunctionHelper.exists(self.props.endCallback)) {
						self.props.endCallback!(self.state.point, true);
					}
				}
			}, ...contextMenu];
		}

		contextMenu = [{
			label: Translate.value("buttons.edit"),
			icon: 'icon icon-resize',
			command(e) {
				self.setState({
					showBoxEditDialog: true
				});
			}
		}, ...contextMenu];

		return (
			<>
				<div className={selectionClasses.join(" ")} style={{
					width: width,
					height: height,
					left: this.state.point.x!,
					top: this.state.point.y!
				}}>
					<div className={"popup" + (!this.state.edit ? " active" : "")}>
						<div className="popup-content">
							<MenuButton {...this.props} icon="icon icon-resize"
										tooltip={Translate.value("buttons.edit")}
										onClick={() => this.setState({
											showBoxEditDialog: true
										})}
							/>
							{this.props.confirmation &&
                                <MenuButton {...this.props} icon="icon icon-check"
                                            tooltip={Translate.value("buttons.confirm")}
                                            onClick={() => {
												if (FunctionHelper.exists(this.props.endCallback)) {
													this.props.endCallback!(this.state.point, true);
												}
											}}
                                />
							}
							<MenuButton {...this.props} icon="icon icon-close"
										tooltip={Translate.value("buttons.cancel")}
										onClick={() => {
											if (FunctionHelper.exists(this.props.endCallback)) {
												this.props.endCallback!(null, false);
											}

											this.setState({
												active: false
											})
										}}
							/>
						</div>
					</div>
					<div className="marker" onPointerDown={this.handleMove} onClick={this.handleClick}
						 onContextMenu={this.handleClick}>
						<span className="icon icon-crosshair"/>
					</div>
				</div>
				<ContextMenu appendTo={document.getElementById("root")} model={contextMenu}
							 ref={this.contextMenu}/>
				<AnnotationEditBoxDialog {...this.props} showHelp={false} parameter={this.state.point}
										 getParent={this.props.getParent} zoomValue={this.props.zoomValue}
										 visible={this.state.showBoxEditDialog}
										 hide={() => this.setState({
											 showBoxEditDialog: false
										 })}
										 submit={(result: Point | Rectangle | Array<Point>) => {
											 if (result instanceof Point) {
												 this.setState({
													 point: result.clone()
												 }, async () => {
													 if (FunctionHelper.exists(this.props.endCallback)) {
														 await this.props.endCallback!(this.state.point, true);
													 }
												 });
											 }
										 }}/>
			</>
		);
	}
}